import {
  WAKE,
  allAreas,
  areaBounds,
  areaKey,
  areaOf,
  areaExists,
  areasNear,
  distanceOutside,
  generateArea,
  generateAsteroidGrid,
  generateRegion,
  forgetPlants,
  rockOrigin,
  sameArea,
  setCell,
  vDist,
  type AreaCoord,
  type AreaRocks,
  type AsteroidBody,
  type AsteroidDef,
  type CurrentDef,
  type Knock,
  type Sim,
  type SphereConfig,
  type Vec2,
} from "@dyson/shared";

/** What the rest of the client does as asteroids come and go. */
export interface WorldHooks {
  added(a: AsteroidBody): void;
  removed(id: string): void;
}

/** Areas are loaded (their rocks known, built or not) this far out, and dropped this much further. */
const AREA_REACH = WAKE.rocksDrop + 400;
const AREA_DROP = AREA_REACH + 1200;

/** An area loaded: its rocks (built or not) and eddies. */
interface Loaded {
  defs: AsteroidDef[];
  currents: CurrentDef[];
}

/**
 * The static world as the client has it. A big sphere is never made whole:
 * only the areas within reach of the player are generated (each rock's
 * definition, which is small), and of their rocks only those near the player
 * are built, tiles and all (see WAKE); the rest are let go again. Edits to
 * asteroids, rocks formed since the world was made, living rocks that died and
 * cut vines are kept whether or not they're built, and put on when they are.
 */
export class ClientWorld {
  readonly sim: Sim;
  private readonly loaded = new Map<string, Loaded>();
  /** Every rock of a loaded area, built or not, by id. */
  private readonly defs = new Map<string, AsteroidDef>();
  private readonly edits = new Map<string, Map<number, number>>();
  /** Rocks formed since the world was made (see NEW_ROCKS), by id. */
  private readonly formed = new Map<string, AsteroidDef>();
  /** Living rocks whose heart was taken. */
  private readonly dead = new Set<string>();
  /** When rocks were last woken and let go (ms, performance.now). */
  private wokeAt = -Infinity;
  /** Rocks are waiting to be built (the last wake ran out of time): wake again next frame. */
  private pending = false;

  constructor(
    readonly sphere: SphereConfig,
    private readonly asteroidById: Map<string, AsteroidBody>,
    private readonly hooks: WorldHooks,
  ) {
    const world = generateRegion(sphere, []);
    world.vines = [];
    this.sim = { world, asteroids: [], ships: new Map(), vinesCut: new Set() };
    asteroidById.clear();
    // The stream's rocks are everywhere at once (where each is follows from the clock, see pathOrigin): known from the start, built as they come near.
    for (const def of world.asteroids) if (def.drift?.stream) this.defs.set(def.id, def);
  }

  /** Rocks near us are still waiting to be built (a wake ran out of time). */
  get building(): boolean {
    return this.pending;
  }

  /** A sphere that's one area is loaded whole, once. */
  private get single(): boolean {
    return this.sphere.areaSize > this.sphere.radius * 2;
  }

  /**
   * Load the areas within reach of `at` and drop those well out of it; then
   * (every WAKE.every s, or when areas came or went) build the rocks near
   * `at` as they are at `time` (see rockOrigin), and let go of those far off.
   * True if any rock was built or let go.
   */
  update(at: Vec2, time: number, knocks?: ReadonlyMap<string, Knock>): boolean {
    let areas = false;
    const want = this.single ? allAreas(this.sphere) : areasNear(this.sphere, at, AREA_REACH);
    for (const a of want) areas = this.load(a) || areas;
    if (!this.single) {
      for (const key of [...this.loaded.keys()]) {
        const [x, y] = key.split(",").map(Number);
        if (distanceOutside(areaBounds(this.sphere, { x: x!, y: y! }), at) < AREA_DROP) continue;
        this.unload(key);
        areas = true;
      }
    }
    const now = performance.now();
    if (!areas && !this.pending && now - this.wokeAt < WAKE.every * 1000) return false;
    this.wokeAt = now;
    return this.wake(at, time, knocks) || areas;
  }

  /**
   * Build the rocks near `at`, nearest first, for up to WAKE.clientBudget ms
   * (the rest next frame: a frame isn't held up), and let go of those far
   * off. True if any came or went.
   */
  private wake(at: Vec2, time: number, knocks?: ReadonlyMap<string, Knock>): boolean {
    let changed = false;
    const want: { def: AsteroidDef; o: Vec2; d: number }[] = [];
    for (const def of this.defs.values()) {
      const built = this.asteroidById.get(def.id);
      const o = def.drift && !built ? rockOrigin(def, time, knocks?.get(def.id)) : { x: def.ox, y: def.oy };
      const d = vDist(at, { x: o.x + def.w / 2, y: o.y + def.h / 2 }) - Math.hypot(def.w, def.h) / 2;
      if (!built && d < WAKE.rocks) want.push({ def, o, d });
      else if (built && d > WAKE.rocksDrop) {
        this.letGo(built);
        changed = true;
      }
    }
    want.sort((p, q) => p.d - q.d);
    const until = performance.now() + WAKE.clientBudget;
    let i = 0;
    for (; i < want.length && (i === 0 || performance.now() < until); i++) {
      const { def, o } = want[i]!;
      if (def.drift) [def.ox, def.oy] = [o.x, o.y];
      this.build(def);
      changed = true;
    }
    this.pending = i < want.length;
    return changed;
  }

  private load(a: AreaCoord): boolean {
    const key = areaKey(a);
    if (this.loaded.has(key) || !areaExists(this.sphere, a)) return false;
    const made = generateArea(this.sphere, a);
    const formed = [...this.formed.values()].filter((d) => sameArea(areaOf(this.sphere, { x: d.ox + d.w / 2, y: d.oy + d.h / 2 }), a));
    const defs = [...made.asteroids, ...formed];
    for (const def of defs) {
      this.defs.set(def.id, def);
      this.sim.world.asteroids.push(def);
    }
    this.sim.world.vines!.push(...made.vines);
    this.sim.world.currents!.push(...made.currents);
    this.loaded.set(key, { defs, currents: made.currents });
    return true;
  }

  private unload(key: string): void {
    const area = this.loaded.get(key);
    if (!area) return;
    this.loaded.delete(key);
    const ids = new Set(area.defs.map((d) => d.id));
    const eddies = new Set(area.currents);
    this.sim.world.currents = this.sim.world.currents!.filter((c) => !eddies.has(c));
    this.sim.world.asteroids = this.sim.world.asteroids.filter((d) => !ids.has(d.id));
    this.sim.world.vines = this.sim.world.vines!.filter((v) => !ids.has(v.rock));
    for (const id of ids) {
      this.defs.delete(id);
      const a = this.asteroidById.get(id);
      if (a) this.letGo(a);
    }
  }

  /** Make an asteroid, with its edits on top, and put it in the world. */
  private build(def: AsteroidDef): void {
    if (this.dead.has(def.id)) def.dead = true;
    const body: AsteroidBody = { def, grid: generateAsteroidGrid(def) };
    for (const [i, cell] of this.edits.get(def.id) ?? []) if (i >= 0 && i < body.grid.tiles.length) setCell(body.grid, i, cell);
    this.asteroidById.set(def.id, body);
    this.sim.asteroids.push(body);
    this.hooks.added(body);
  }

  /** Take a built asteroid out of the world (its edits are kept, for when it's built again). */
  private letGo(a: AsteroidBody): void {
    forgetPlants(a.def);
    this.asteroidById.delete(a.def.id);
    this.sim.asteroids = this.sim.asteroids.filter((x) => x !== a);
    this.hooks.removed(a.def.id);
  }

  /** What an area says of its asteroids (see AreaRocks): rocks formed there, dead rocks, cut vines. Edits are taken one by one (see edit). */
  takeRocks(rocks: AreaRocks): void {
    for (const id of rocks.dead) this.kill(id);
    for (const def of rocks.formed) this.addRock(def);
    for (const id of rocks.vinesCut) this.sim.vinesCut!.add(id);
  }

  /** A rock formed (see NEW_ROCKS): kept, and known (built when we're near) if its area is loaded. */
  addRock(def: AsteroidDef): void {
    if (this.formed.has(def.id)) return;
    this.formed.set(def.id, def);
    const area = this.loaded.get(areaKey(areaOf(this.sphere, { x: def.ox + def.w / 2, y: def.oy + def.h / 2 })));
    if (!area || this.defs.has(def.id)) return;
    area.defs.push(def);
    this.defs.set(def.id, def);
    this.sim.world.asteroids.push(def);
    this.wokeAt = -Infinity;
  }

  /** A living rock died (its heart was taken): made again as a husk, if it's built. */
  kill(id: string): void {
    if (this.dead.has(id)) return;
    this.dead.add(id);
    const a = this.asteroidById.get(id);
    if (!a || a.def.dead) return;
    this.letGo(a);
    this.build(a.def);
  }

  /** A vine was cut through, or grew back. */
  setVine(id: string, cut: boolean): void {
    if (cut) this.sim.vinesCut!.add(id);
    else this.sim.vinesCut!.delete(id);
  }

  /** An asteroid tile as an area says it is (built or not), as a cell (the tile and its shape, see packCell). Returns the asteroid if it's built. */
  edit(id: string, index: number, cell: number): AsteroidBody | undefined {
    let m = this.edits.get(id);
    if (!m) this.edits.set(id, (m = new Map()));
    m.set(index, cell);
    const a = this.asteroidById.get(id);
    if (a && index >= 0 && index < a.grid.tiles.length) setCell(a.grid, index, cell);
    return a;
  }
}
